// Creator mode shortcut: hold Option (Alt) and press 1, 2, 3 in order.
// Matches on event.code because on a Mac Option+1 types "¡", so event.key never reads "1".

export const CREATOR_MODE_SEQUENCE = ["Digit1", "Digit2", "Digit3"] as const;
/** Max gap between key presses before the sequence starts over. */
export const CREATOR_MODE_STEP_TIMEOUT_MS = 1500;

export interface CreatorModeSequence {
  /** How many keys of the sequence have been matched so far. */
  step: number;
  /** Time of the last matched key press. */
  lastAt: number;
}

export const EMPTY_CREATOR_MODE_SEQUENCE: CreatorModeSequence = { step: 0, lastAt: 0 };

interface ShortcutKeyEvent {
  code: string;
  altKey: boolean;
  ctrlKey: boolean;
  metaKey: boolean;
}

/** True when the key press comes from a text field, so typing never trips the shortcut. */
export const isTypingTarget = (target: EventTarget | null): boolean => {
  if (!target || typeof (target as HTMLElement).tagName !== "string") return false;
  const el = target as HTMLElement;
  const tag = el.tagName.toLowerCase();
  return tag === "input" || tag === "textarea" || tag === "select" || el.isContentEditable;
};

/** Moves the shortcut sequence on by one key press; `toggle` is true when the full sequence has just been entered. */
export const advanceCreatorModeSequence = (
  sequence: CreatorModeSequence,
  event: ShortcutKeyEvent,
  now: number
): { sequence: CreatorModeSequence; toggle: boolean } => {
  // The Option key itself fires a keydown; let it pass without resetting the sequence
  if (event.code === "AltLeft" || event.code === "AltRight") return { sequence, toggle: false };

  const isPlainOption = event.altKey && !event.ctrlKey && !event.metaKey;
  if (!isPlainOption) return { sequence: EMPTY_CREATOR_MODE_SEQUENCE, toggle: false };

  const expired = sequence.step > 0 && now - sequence.lastAt > CREATOR_MODE_STEP_TIMEOUT_MS;
  const step = expired ? 0 : sequence.step;

  if (event.code === CREATOR_MODE_SEQUENCE[step]) {
    const nextStep = step + 1;
    if (nextStep === CREATOR_MODE_SEQUENCE.length) {
      return { sequence: EMPTY_CREATOR_MODE_SEQUENCE, toggle: true };
    }
    return { sequence: { step: nextStep, lastAt: now }, toggle: false };
  }

  // A wrong key starts over, but a fresh "1" still counts as the first step
  if (event.code === CREATOR_MODE_SEQUENCE[0]) {
    return { sequence: { step: 1, lastAt: now }, toggle: false };
  }
  return { sequence: EMPTY_CREATOR_MODE_SEQUENCE, toggle: false };
};
